import { useState, useEffect, useCallback } from 'react';
import { Calendar, ChevronLeft, ChevronRight, Plus, Clock, MapPin, X, RefreshCw } from 'lucide-react';
import { theme } from '../../lib/theme';
import { supabase } from '../../lib/supabase';
import LoadingSpinner from '../common/LoadingSpinner';
import AgentFilterCombo from '../common/AgentFilterCombo';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const VIEW_MODES = { MONTH: 'month', WEEK: 'week', DAY: 'day' };

export default function CalendarView() {
  const [viewMode, setViewMode] = useState(VIEW_MODES.MONTH);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [showings, setShowings] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newEvent, setNewEvent] = useState({ title: '', date: '', start_time: '09:00', end_time: '10:00', type: 'event' });
  // ── Agent scoping (broker/admin only) ──
  const [agents, setAgents] = useState([]);
  const [agentFilter, setAgentFilter] = useState('mine'); // 'mine' | 'all' | agent UUID
  const [isBroker, setIsBroker] = useState(false);

  const token = localStorage?.getItem?.('sb-token') || '';

  // Load agents + detect role on mount
  useEffect(() => {
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;
        const r = await fetch('/api/team', { headers: { Authorization: `Bearer ${session.access_token}` } });
        if (r.ok) {
          const data = await r.json();
          const members = data.team || data.members || [];
          setAgents(members.map(u => ({
            id: u.id,
            name: u.full_name || u.name || u.email,
          })));
          setIsBroker(true); // /api/team only succeeds for broker/admin
        }
      } catch { /* agent role — no filter */ }
    })();
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    const start = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const end = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    const startStr = start.toISOString().split('T')[0];
    const endStr = end.toISOString().split('T')[0];

    try {
      // Build agent_id param for broker cross-agent view
      let agentParam = '';
      if (isBroker && agentFilter === 'mine') agentParam = '&agent_id=me';
      else if (isBroker && agentFilter && agentFilter !== 'all') agentParam = `&agent_id=${agentFilter}`;
      // agentFilter === 'all' → fetch all (no agent_id filter for tasks; calendar APIs don't support 'all' yet, use own)

      const [googleRes, msRes, showingsRes, tasksRes] = await Promise.allSettled([
        fetch(`/api/google-calendar?start=${startStr}&end=${endStr}${agentParam}`, { headers }),
        fetch(`/api/microsoft-calendar?start=${startStr}&end=${endStr}&days=31${agentParam}`, { headers }),
        fetch(`/api/showings?upcoming=true`, { headers }),
        fetch(`/api/tasks?range=all${agentParam}`, { headers }),
      ]);

      const allEvents = [];
      if (googleRes.status === 'fulfilled' && googleRes.value.ok) {
        const data = await googleRes.value.json();
        (data.events || []).forEach(e => allEvents.push({ ...e, source: 'google', color: '#4285f4' }));
      }
      if (msRes.status === 'fulfilled' && msRes.value.ok) {
        const data = await msRes.value.json();
        (data.events || []).forEach(e => allEvents.push({ ...e, source: 'microsoft', color: '#00a4ef' }));
      }
      setEvents(allEvents);

      if (showingsRes.status === 'fulfilled' && showingsRes.value.ok) {
        const data = await showingsRes.value.json();
        setShowings(data.showings || []);
      }
      if (tasksRes.status === 'fulfilled' && tasksRes.value.ok) {
        const data = await tasksRes.value.json();
        setTasks(data.tasks || []);
      }
    } catch (e) {
      console.error('Calendar fetch error:', e);
    }
    setLoading(false);
  }, [currentDate, token, agentFilter, isBroker]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const navigate = (dir) => {
    const d = new Date(currentDate);
    if (viewMode === VIEW_MODES.MONTH) d.setMonth(d.getMonth() + dir);
    else if (viewMode === VIEW_MODES.WEEK) d.setDate(d.getDate() + dir * 7);
    else d.setDate(d.getDate() + dir);
    setCurrentDate(d);
  };

  const goToday = () => setCurrentDate(new Date());

  const getDateItems = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    const items = [];

    events.forEach(e => {
      const eDate = (e.start || e.scheduled_at || '').slice(0, 10);
      if (eDate === dateStr) items.push({ ...e, itemType: 'event' });
    });
    showings.forEach(s => {
      const sDate = (s.scheduled_at || '').slice(0, 10);
      if (sDate === dateStr) items.push({
        title: `Showing: ${(s.property_address || '').slice(0, 30)}`,
        start: s.scheduled_at,
        itemType: 'showing',
        color: theme.colors.success,
        data: s,
      });
    });
    tasks.forEach(t => {
      if ((t.due_date || '').slice(0, 10) === dateStr) items.push({
        title: t.title,
        itemType: 'task',
        color: t.priority === 'high' ? theme.colors.danger : theme.colors.warning,
        data: t,
      });
    });

    return items;
  };

  const renderMonthView = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const today = new Date().toISOString().split('T')[0];

    const cells = [];

    // Empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      cells.push(
        <div
          key={`empty-${i}`}
          style={{
            minHeight: 100,
            padding: 8,
            borderBottom: `1px solid ${theme.colors.border}`,
            borderRight: `1px solid ${theme.colors.border}`,
            backgroundColor: theme.colors.bg,
            opacity: 0.4,
          }}
        />
      );
    }

    // Day cells
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateStr = date.toISOString().split('T')[0];
      const isToday = dateStr === today;
      const items = getDateItems(date);

      cells.push(
        <div
          key={day}
          onClick={() => { setCurrentDate(date); setViewMode(VIEW_MODES.DAY); }}
          style={{
            minHeight: 100,
            padding: 8,
            borderBottom: `1px solid ${theme.colors.border}`,
            borderRight: `1px solid ${theme.colors.border}`,
            backgroundColor: isToday ? `${theme.colors.primary}08` : theme.colors.card,
            cursor: 'pointer',
            transition: 'background-color 0.2s ease',
          }}
          onMouseEnter={(e) => {
            if (!isToday) e.currentTarget.style.backgroundColor = theme.colors.bg;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = isToday ? `${theme.colors.primary}08` : theme.colors.card;
          }}
        >
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              marginBottom: 6,
              color: isToday ? theme.colors.primary : theme.colors.text,
              padding: isToday ? '2px 6px' : '0px',
              borderRadius: isToday ? 4 : 0,
              backgroundColor: isToday ? theme.colors.primary : 'transparent',
              color: isToday ? '#FFFFFF' : theme.colors.text,
              display: 'inline-block',
              minWidth: 24,
              textAlign: 'center',
            }}
          >
            {day}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {items.slice(0, 3).map((item, idx) => (
              <div
                key={idx}
                onClick={(e) => { e.stopPropagation(); setSelectedEvent(item); }}
                style={{
                  fontSize: 12,
                  paddingLeft: 6,
                  paddingRight: 6,
                  paddingTop: 3,
                  paddingBottom: 3,
                  borderRadius: theme.radius.sm,
                  backgroundColor: `${item.color || theme.colors.info}20`,
                  color: item.color || theme.colors.info,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  borderLeft: `3px solid ${item.color || theme.colors.info}`,
                  cursor: 'pointer',
                }}
              >
                {item.title || item.subject || 'Event'}
              </div>
            ))}
            {items.length > 3 && (
              <div style={{ fontSize: 11, color: theme.colors.textLight, paddingLeft: 6 }}>
                +{items.length - 3} more
              </div>
            )}
          </div>
        </div>
      );
    }

    // If no events in the month, show empty state
    const hasEvents = events.length > 0 || showings.length > 0 || tasks.length > 0;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div
          style={{
            border: `1px solid ${theme.colors.border}`,
            borderRadius: theme.radius.md,
            overflow: 'hidden',
            boxShadow: theme.shadow.sm,
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              backgroundColor: theme.colors.bg,
              borderBottom: `1px solid ${theme.colors.border}`,
            }}
          >
            {DAYS.map(d => (
              <div
                key={d}
                style={{
                  textAlign: 'center',
                  fontSize: 12,
                  fontWeight: 600,
                  color: theme.colors.textLight,
                  paddingTop: 12,
                  paddingBottom: 12,
                  borderRight: `1px solid ${theme.colors.border}`,
                }}
              >
                {d}
              </div>
            ))}
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
            }}
          >
            {cells}
          </div>
        </div>

        {!hasEvents && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '60px 20px',
              textAlign: 'center',
            }}
          >
            <Calendar
              size={48}
              color={theme.colors.textLight}
              style={{ marginBottom: 16, opacity: 0.5 }}
            />
            <h3
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: theme.colors.text,
                marginBottom: 4,
              }}
            >
              No events this month
            </h3>
            <p
              style={{
                fontSize: 14,
                color: theme.colors.textLight,
              }}
            >
              Connect Google or Outlook calendar to see your schedule here.
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderDayView = () => {
    const items = getDateItems(currentDate);
    const hours = Array.from({ length: 14 }, (_, i) => i + 7);

    return (
      <div
        style={{
          border: `1px solid ${theme.colors.border}`,
          borderRadius: theme.radius.md,
          overflow: 'hidden',
          boxShadow: theme.shadow.sm,
        }}
      >
        {hours.map(hour => {
          const hourItems = items.filter(item => {
            const startStr = item.start || item.scheduled_at || '';
            if (!startStr) return false;
            return new Date(startStr).getHours() === hour;
          });

          return (
            <div
              key={hour}
              style={{
                display: 'flex',
                borderBottom: `1px solid ${theme.colors.border}`,
                minHeight: 48,
              }}
            >
              <div
                style={{
                  width: 64,
                  fontSize: 12,
                  color: theme.colors.textLight,
                  paddingTop: 8,
                  paddingBottom: 8,
                  paddingLeft: 8,
                  paddingRight: 8,
                  borderRight: `1px solid ${theme.colors.border}`,
                  flexShrink: 0,
                  backgroundColor: theme.colors.bg,
                  fontWeight: 500,
                }}
              >
                {hour > 12 ? `${hour - 12}pm` : hour === 12 ? '12pm' : `${hour}am`}
              </div>
              <div style={{ flex: 1, padding: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {hourItems.map((item, idx) => (
                  <div
                    key={idx}
                    onClick={() => setSelectedEvent(item)}
                    style={{
                      fontSize: 14,
                      paddingLeft: 12,
                      paddingRight: 12,
                      paddingTop: 8,
                      paddingBottom: 8,
                      borderRadius: theme.radius.sm,
                      backgroundColor: `${item.color || theme.colors.info}15`,
                      borderLeft: `3px solid ${item.color || theme.colors.info}`,
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = `${item.color || theme.colors.info}25`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = `${item.color || theme.colors.info}15`;
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 600,
                        color: item.color || theme.colors.info,
                        marginBottom: 2,
                      }}
                    >
                      {item.title || item.subject}
                    </div>
                    {item.itemType === 'showing' && item.data && (
                      <div style={{ fontSize: 12, color: theme.colors.textLight }}>
                        {item.data.client_name || 'Client'}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const handleCreate = async () => {
    if (!newEvent.title || !newEvent.date) return;
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
    const startDate = `${newEvent.date}T${newEvent.start_time}:00`;
    const endDate = `${newEvent.date}T${newEvent.end_time}:00`;

    if (newEvent.type === 'showing') {
      await fetch('/api/showings?action=create', {
        method: 'POST', headers,
        body: JSON.stringify({
          property_address: newEvent.title,
          scheduled_at: startDate,
          duration_minutes: (new Date(endDate) - new Date(startDate)) / 60000,
        }),
      });
    } else {
      const calResp = await fetch('/api/google-calendar', {
        method: 'POST', headers,
        body: JSON.stringify({ subject: newEvent.title, start: startDate, end: endDate }),
      });
      if (!calResp.ok) {
        await fetch('/api/microsoft-calendar', {
          method: 'POST', headers,
          body: JSON.stringify({ subject: newEvent.title, start: startDate, end: endDate }),
        });
      }
    }

    setShowCreateModal(false);
    setNewEvent({ title: '', date: '', start_time: '09:00', end_time: '10:00', type: 'event' });
    fetchData();
  };

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
      {loading && <LoadingSpinner variant="section" label="Loading calendar..." />}

      {!loading && (
        <>
          {/* Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 24,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <h1
                style={{
                  fontSize: 28,
                  fontWeight: 700,
                  color: theme.colors.text,
                  margin: 0,
                }}
              >
                Calendar
              </h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  onClick={() => navigate(-1)}
                  style={{
                    padding: 8,
                    borderRadius: theme.radius.sm,
                    border: 'none',
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                    color: theme.colors.textLight,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background-color 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = theme.colors.bg;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  onClick={goToday}
                  style={{
                    paddingLeft: 12,
                    paddingRight: 12,
                    paddingTop: 6,
                    paddingBottom: 6,
                    fontSize: 13,
                    fontWeight: 600,
                    color: theme.colors.text,
                    backgroundColor: theme.colors.card,
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: theme.radius.sm,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = theme.colors.bg;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = theme.colors.card;
                  }}
                >
                  Today
                </button>
                <button
                  onClick={() => navigate(1)}
                  style={{
                    padding: 8,
                    borderRadius: theme.radius.sm,
                    border: 'none',
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                    color: theme.colors.textLight,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background-color 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = theme.colors.bg;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <ChevronRight size={20} />
                </button>
              </div>
              <h2
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  color: theme.colors.textLight,
                  margin: 0,
                }}
              >
                {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
                {viewMode === VIEW_MODES.DAY && `, ${currentDate.getDate()}`}
              </h2>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {/* Agent filter — broker/admin only */}
              {isBroker && agents.length > 0 && (
                <AgentFilterCombo
                  agents={agents}
                  value={agentFilter}
                  onChange={setAgentFilter}
                  mineLabel="My Calendar"
                  allLabel="All Agents"
                />
              )}
              <div
                style={{
                  display: 'flex',
                  backgroundColor: theme.colors.bg,
                  borderRadius: theme.radius.md,
                  padding: 4,
                  gap: 0,
                }}
              >
                {Object.entries(VIEW_MODES).map(([key, val]) => (
                  <button
                    key={key}
                    onClick={() => setViewMode(val)}
                    style={{
                      paddingLeft: 12,
                      paddingRight: 12,
                      paddingTop: 6,
                      paddingBottom: 6,
                      fontSize: 13,
                      fontWeight: 600,
                      borderRadius: theme.radius.sm,
                      border: 'none',
                      backgroundColor: viewMode === val ? theme.colors.card : 'transparent',
                      color: viewMode === val ? theme.colors.text : theme.colors.textLight,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      boxShadow: viewMode === val ? theme.shadow.sm : 'none',
                    }}
                    onMouseEnter={(e) => {
                      if (viewMode !== val) {
                        e.currentTarget.style.color = theme.colors.text;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (viewMode !== val) {
                        e.currentTarget.style.color = theme.colors.textLight;
                      }
                    }}
                  >
                    {key.charAt(0) + key.slice(1).toLowerCase()}
                  </button>
                ))}
              </div>
              <button
                onClick={() => { setNewEvent(e => ({ ...e, date: currentDate.toISOString().split('T')[0] })); setShowCreateModal(true); }}
                style={{
                  paddingLeft: 16,
                  paddingRight: 16,
                  paddingTop: 8,
                  paddingBottom: 8,
                  backgroundColor: theme.colors.primary,
                  color: '#FFFFFF',
                  fontSize: 13,
                  fontWeight: 600,
                  border: 'none',
                  borderRadius: theme.radius.md,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  transition: 'background-color 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = theme.colors.primaryLight;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = theme.colors.primary;
                }}
              >
                <Plus size={16} />
                New Event
              </button>
            </div>
          </div>

          {/* Sync sources */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              marginBottom: 16,
              fontSize: 12,
              color: theme.colors.textLight,
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: '#4285f4',
                }}
              />
              Google
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: '#00a4ef',
                }}
              />
              Microsoft
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: theme.colors.success,
                }}
              />
              Showings
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: theme.colors.warning,
                }}
              />
              Tasks
            </span>
            {loading && (
              <span style={{ color: theme.colors.info, display: 'flex', alignItems: 'center', gap: 6 }}>
                <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} />
                Syncing...
              </span>
            )}
          </div>

          <style>{`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}</style>

          {/* Calendar grid */}
          {viewMode === VIEW_MODES.MONTH && renderMonthView()}
          {viewMode === VIEW_MODES.DAY && renderDayView()}
          {viewMode === VIEW_MODES.WEEK && renderMonthView()}

          {/* Event detail modal */}
          {selectedEvent && (
            <div
              onClick={() => setSelectedEvent(null)}
              style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.4)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 50,
              }}
            >
              <div
                onClick={e => e.stopPropagation()}
                style={{
                  backgroundColor: theme.colors.card,
                  borderRadius: theme.radius.lg,
                  boxShadow: theme.shadow.lg,
                  padding: 24,
                  maxWidth: 400,
                  width: '100%',
                  marginLeft: 16,
                  marginRight: 16,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    marginBottom: 16,
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: 0.05,
                        marginBottom: 4,
                        color: selectedEvent.color || theme.colors.info,
                      }}
                    >
                      {selectedEvent.itemType || selectedEvent.source || 'Event'}
                    </div>
                    <h3
                      style={{
                        fontSize: 18,
                        fontWeight: 600,
                        color: theme.colors.text,
                        margin: 0,
                      }}
                    >
                      {selectedEvent.title || selectedEvent.subject}
                    </h3>
                  </div>
                  <button
                    onClick={() => setSelectedEvent(null)}
                    style={{
                      backgroundColor: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      color: theme.colors.textLight,
                      padding: 4,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'color 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = theme.colors.text;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = theme.colors.textLight;
                    }}
                  >
                    <X size={20} />
                  </button>
                </div>
                {selectedEvent.start && (
                  <p
                    style={{
                      fontSize: 13,
                      color: theme.colors.textLight,
                      marginBottom: 8,
                      margin: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <Clock size={16} />
                    {new Date(selectedEvent.start).toLocaleString()}
                    {selectedEvent.end && ` — ${new Date(selectedEvent.end).toLocaleTimeString()}`}
                  </p>
                )}
                {selectedEvent.data?.property_address && (
                  <p
                    style={{
                      fontSize: 13,
                      color: theme.colors.textLight,
                      marginBottom: 8,
                      margin: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <MapPin size={16} />
                    {selectedEvent.data.property_address}
                  </p>
                )}
                {selectedEvent.data?.client_name && (
                  <p
                    style={{
                      fontSize: 13,
                      color: theme.colors.textLight,
                      margin: 0,
                    }}
                  >
                    Client: {selectedEvent.data.client_name}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Create event modal */}
          {showCreateModal && (
            <div
              onClick={() => setShowCreateModal(false)}
              style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.4)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 50,
              }}
            >
              <div
                onClick={e => e.stopPropagation()}
                style={{
                  backgroundColor: theme.colors.card,
                  borderRadius: theme.radius.lg,
                  boxShadow: theme.shadow.lg,
                  padding: 24,
                  maxWidth: 400,
                  width: '100%',
                  marginLeft: 16,
                  marginRight: 16,
                }}
              >
                <h3
                  style={{
                    fontSize: 18,
                    fontWeight: 600,
                    color: theme.colors.text,
                    marginBottom: 16,
                    margin: 0,
                  }}
                >
                  New Event
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div>
                    <label
                      style={{
                        display: 'block',
                        fontSize: 13,
                        fontWeight: 600,
                        color: theme.colors.text,
                        marginBottom: 6,
                      }}
                    >
                      Type
                    </label>
                    <select
                      value={newEvent.type}
                      onChange={e => setNewEvent(ev => ({ ...ev, type: e.target.value }))}
                      style={{
                        width: '100%',
                        border: `1px solid ${theme.colors.border}`,
                        borderRadius: theme.radius.sm,
                        paddingLeft: 12,
                        paddingRight: 12,
                        paddingTop: 8,
                        paddingBottom: 8,
                        fontSize: 13,
                        color: theme.colors.text,
                        backgroundColor: theme.colors.card,
                      }}
                    >
                      <option value="event">Calendar Event</option>
                      <option value="showing">Showing</option>
                    </select>
                  </div>
                  <div>
                    <label
                      style={{
                        display: 'block',
                        fontSize: 13,
                        fontWeight: 600,
                        color: theme.colors.text,
                        marginBottom: 6,
                      }}
                    >
                      {newEvent.type === 'showing' ? 'Property Address' : 'Title'}
                    </label>
                    <input
                      type="text"
                      value={newEvent.title}
                      onChange={e => setNewEvent(ev => ({ ...ev, title: e.target.value }))}
                      style={{
                        width: '100%',
                        border: `1px solid ${theme.colors.border}`,
                        borderRadius: theme.radius.sm,
                        paddingLeft: 12,
                        paddingRight: 12,
                        paddingTop: 8,
                        paddingBottom: 8,
                        fontSize: 13,
                        color: theme.colors.text,
                        backgroundColor: theme.colors.card,
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: 'block',
                        fontSize: 13,
                        fontWeight: 600,
                        color: theme.colors.text,
                        marginBottom: 6,
                      }}
                    >
                      Date
                    </label>
                    <input
                      type="date"
                      value={newEvent.date}
                      onChange={e => setNewEvent(ev => ({ ...ev, date: e.target.value }))}
                      style={{
                        width: '100%',
                        border: `1px solid ${theme.colors.border}`,
                        borderRadius: theme.radius.sm,
                        paddingLeft: 12,
                        paddingRight: 12,
                        paddingTop: 8,
                        paddingBottom: 8,
                        fontSize: 13,
                        color: theme.colors.text,
                        backgroundColor: theme.colors.card,
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <label
                        style={{
                          display: 'block',
                          fontSize: 13,
                          fontWeight: 600,
                          color: theme.colors.text,
                          marginBottom: 6,
                        }}
                      >
                        Start
                      </label>
                      <input
                        type="time"
                        value={newEvent.start_time}
                        onChange={e => setNewEvent(ev => ({ ...ev, start_time: e.target.value }))}
                        style={{
                          width: '100%',
                          border: `1px solid ${theme.colors.border}`,
                          borderRadius: theme.radius.sm,
                          paddingLeft: 12,
                          paddingRight: 12,
                          paddingTop: 8,
                          paddingBottom: 8,
                          fontSize: 13,
                          color: theme.colors.text,
                          backgroundColor: theme.colors.card,
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: 'block',
                          fontSize: 13,
                          fontWeight: 600,
                          color: theme.colors.text,
                          marginBottom: 6,
                        }}
                      >
                        End
                      </label>
                      <input
                        type="time"
                        value={newEvent.end_time}
                        onChange={e => setNewEvent(ev => ({ ...ev, end_time: e.target.value }))}
                        style={{
                          width: '100%',
                          border: `1px solid ${theme.colors.border}`,
                          borderRadius: theme.radius.sm,
                          paddingLeft: 12,
                          paddingRight: 12,
                          paddingTop: 8,
                          paddingBottom: 8,
                          fontSize: 13,
                          color: theme.colors.text,
                          backgroundColor: theme.colors.card,
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    style={{
                      flex: 1,
                      paddingLeft: 16,
                      paddingRight: 16,
                      paddingTop: 8,
                      paddingBottom: 8,
                      border: `1px solid ${theme.colors.border}`,
                      borderRadius: theme.radius.md,
                      fontSize: 13,
                      fontWeight: 600,
                      color: theme.colors.text,
                      backgroundColor: theme.colors.card,
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = theme.colors.bg;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = theme.colors.card;
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreate}
                    style={{
                      flex: 1,
                      paddingLeft: 16,
                      paddingRight: 16,
                      paddingTop: 8,
                      paddingBottom: 8,
                      backgroundColor: theme.colors.primary,
                      color: '#FFFFFF',
                      border: 'none',
                      borderRadius: theme.radius.md,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = theme.colors.primaryLight;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = theme.colors.primary;
                    }}
                  >
                    Create
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
